import React, { ReactNode, JSX } from 'react';
import { observer } from 'mobx-react';
import {
  ApiAirportType,
  ApiBaseData,
  ApiGeoUnit,
  ApiItemType,
  ApiRoomContainer,
  ApiSearchProductDataResponse,
  ApiSeason
} from '@ibe/api';
import PackageSearchStore from './PackageSearchStore';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/PackageSearch.en.t.json';
import Keys from '../../../_Translations/generated/PackageSearch.en.t.json.keys';
import { Label } from 'reactstrap';
import { Select } from '../../Components/Form/Select/Select';
import GeoUnitSelectAsync from '../../Components/Input/GeoUnitSelectAsync/GeoUnitSelectAsync';
import BestPriceAvailabilitySearchStore from '../BestPriceAvailabilitySearch/BestPriceAvailabilitySearchStore';
import GeoUnitCheckboxAsync from '../../Components/Input/GeoUnitCheckboxAsync/GeoUnitCheckboxAsync';
import DestinationItem from '../../../Models/DestinationItem';
import ProductSelectAsync from '../../Components/Input/ProductSelectAsync/ProductSelectAsync';
import DateRangeInput from '../../Components/Input/DateRangeInput/DateRangeInput';
import useConfig from '../../Hooks/useConfig';
import { DurationFactory } from '@ibe/services';
import { DateRangeDurationPropsType } from './PackageSearch';
import DatePicker from '../../Components/Input/DatePicker/DatePicker';
import {
  PaxRoomSelection,
  PaxRoomSelectionImperativeHandleRefType
} from '../../Components/UI/PaxRoomSelection/PaxRoomSelection';
import DateRangeInputSingleField from '../../Components/Input/DateRangeInputSingleField/DateRangeInputSingleField';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { WeekDayConstraints } from '../../Components/Input/DateRangeInput/DateRangeInputProps';
import PackageSearchValidationErrorMessage from './PackageSearchValidationErrorMessage';
import { ChangeType } from '../../Modules/Package/externals';

export const CategorySelect = observer(function CategorySelect(props: {
  categories: ApiBaseData[];
  showLabels?: boolean;
  store: PackageSearchStore;
}): JSX.Element {
  const { categories, showLabels, store } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);

  const handleTypeChange = (code: string): void => {
    store.setPackageServiceGroup(code);
  };

  return (
    <>
      {showLabels && <Label htmlFor="packageServiceGroup">{t(Keys.category)}</Label>}
      <Select
        selectedOption={categories.find(option => option.code === store.packageServiceGroup)}
        getLabel={(option): string => (option ? option.description : '')}
        getValue={(option): string => (option ? option.code : '')}
        onChange={(val): void => handleTypeChange(val.code)}
        options={categories}
        id="packageServiceGroupInput"
      />
    </>
  );
});

export const OriginSelect = observer(function OriginSelect(props: {
  showLabels?: boolean;
  store: PackageSearchStore;
  selectLabelSort?: boolean;
  onPackageSearchChange?: (a: { store?: PackageSearchStore; changeType: ChangeType }) => void;
}): JSX.Element {
  const { showLabels, store, selectLabelSort, onPackageSearchChange } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);
  return (
    <>
      {showLabels && <Label htmlFor="regionInput">{t(Keys.origin)}</Label>}
      <GeoUnitSelectAsync
        dataTestId="packageOriginSelect"
        placeholder={t(Keys.origin)}
        itemType={ApiItemType.TRANSPORT}
        value={store.origin}
        onSelect={(geoUnit?: ApiGeoUnit): void => {
          store.setOrigin(geoUnit);
          if (onPackageSearchChange) {
            onPackageSearchChange({ changeType: 'origins', store });
          }
        }}
        selectLabelSort={selectLabelSort}
      />
    </>
  );
});

export const OriginCheckboxes = observer(function OriginCheckboxes(props: {
  showLabels?: boolean;
  store: PackageSearchStore | BestPriceAvailabilitySearchStore;
  externalGeoUnits?: ApiGeoUnit[];
  changeOnBlur?: boolean;
  withSelectAll?: boolean;
  withRemoveAll?: boolean;
  showPseudo?: boolean;
  originMustBeSet?: boolean;
  bestPriceSubmitOnChange?: boolean;
  onSubmit?: () => void;
  valueToShow?: string;
  onPackageSearchChange?: (a: { store?: PackageSearchStore; changeType: ChangeType }) => void;
}): JSX.Element {
  const {
    showLabels,
    store,
    externalGeoUnits,
    changeOnBlur,
    withSelectAll,
    showPseudo,
    withRemoveAll,
    originMustBeSet = true,
    bestPriceSubmitOnChange,
    onSubmit,
    valueToShow,
    onPackageSearchChange
  } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);
  return (
    <>
      {showLabels && <Label htmlFor="regionInput">{t(Keys.origin)}</Label>}
      <GeoUnitCheckboxAsync
        placeholder={!originMustBeSet ? t(Keys.arbitrary) : t(Keys.select)}
        itemType={ApiItemType.TRANSPORT}
        airportType={ApiAirportType.ORIGIN}
        value={store.origins}
        onChange={(geoUnits: ApiGeoUnit[]): void => {
          store.setOrigins(geoUnits);
          if (bestPriceSubmitOnChange && !!onSubmit) {
            onSubmit();
          }
          if (onPackageSearchChange) {
            onPackageSearchChange({ changeType: 'origins' });
          }
        }}
        externalGeoUnits={externalGeoUnits}
        changeOnBlur={changeOnBlur}
        withSelectAll={withSelectAll}
        showPseudo={showPseudo}
        withRemoveAll={withRemoveAll}
        valueToShow={valueToShow}
      />
    </>
  );
});

export const IndexedDestinationSelect = observer(function IndexedDestinationSelect(props: {
  showLabels?: boolean;
  store: PackageSearchStore;
  renderLabel?: (option: DestinationItem, searchTerm?: string) => JSX.Element;
  optionsMapper?: (data: ApiSearchProductDataResponse, searchTerm?: string) => DestinationItem[];
  onPackageSearchChange?: (a: { store?: PackageSearchStore; changeType: ChangeType }) => void;
}): JSX.Element {
  const { showLabels, store, renderLabel, optionsMapper, onPackageSearchChange } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);
  return (
    <>
      {showLabels && <Label htmlFor="regionInput">{t(Keys.destination)}</Label>}
      <ProductSelectAsync
        placeholder={t(Keys.select)}
        emptySearchAllowed
        renderLabel={renderLabel}
        optionsMapper={optionsMapper}
        dataTestId="packageDestinationInput"
        name="productInput"
        itemType={ApiItemType.HOTEL}
        hideAnywhereGeoUnit
        value={store.destinationItem}
        onSelect={(item: DestinationItem | null): void => {
          store.setDestinationItem(item || undefined);
          if (onPackageSearchChange) {
            onPackageSearchChange({ changeType: 'destination', store });
          }
        }}
      />
    </>
  );
});

export const DestinationSelect = observer(function DestinationSelect(props: {
  showLabels?: boolean;
  store: PackageSearchStore;
  subType: ApiItemType;
  selectLabelSort?: boolean;
}): JSX.Element {
  const { showLabels, store, subType, selectLabelSort } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);
  return (
    <>
      {showLabels && <Label htmlFor="regionInput">{t(Keys.destination)}</Label>}
      <GeoUnitSelectAsync
        dataTestId="packageDestinationSelect"
        value={store.destination}
        itemType={subType}
        onSelect={(geoUnit?: ApiGeoUnit): void => store.setDestination(geoUnit)}
        selectLabelSort={selectLabelSort}
      />
    </>
  );
});

export const DateRangePicker = observer(function DateRangePicker(props: {
  customDateFormat?: boolean;
  showLabels?: boolean;
  store?: PackageSearchStore | BestPriceAvailabilitySearchStore;
  withDateRangePicker?: boolean;
  minDateRangeSpan?: number;
  maxDateRangeSpan?: number;
  seasons?: ApiSeason[];
  noDatePickerOverflow?: boolean;
  onDateRangeChange?: (start?: string, end?: string) => void;
  onPackageSearchChange?: (a: { store?: PackageSearchStore; changeType: ChangeType }) => void;
  startDate?: string;
  endDate?: string;
  ignoreMinDate?: boolean;
  updateStateFromProps?: boolean;
  disabled?: boolean;
  minDateOffset?: number;
  minDate?: Date;
  maxDate?: Date;
  weekDayConstraints?: WeekDayConstraints[];
  durationProps?: DateRangeDurationPropsType;
  ignoreStartDateForFilter?: boolean;
  selectsDisabledDaysInRange?: boolean;
  useAdvancedFeatures?: boolean;
  focusSelectedMonth?: boolean;
  fillWithLastDateRangeWhenNoSelection?: boolean;
  clearSelectionsOnOpen?: boolean;
  showDateSelectionInModal?: boolean;
  modalLabel?: string;
}): JSX.Element {
  const {
    customDateFormat,
    showLabels,
    store,
    withDateRangePicker,
    minDateRangeSpan,
    maxDateRangeSpan,
    seasons,
    noDatePickerOverflow,
    startDate,
    endDate,
    onDateRangeChange,
    onPackageSearchChange,
    ignoreMinDate,
    updateStateFromProps,
    disabled,
    minDateOffset,
    minDate,
    maxDate,
    weekDayConstraints,
    durationProps,
    ignoreStartDateForFilter,
    selectsDisabledDaysInRange,
    useAdvancedFeatures,
    focusSelectedMonth,
    fillWithLastDateRangeWhenNoSelection,
    clearSelectionsOnOpen,
    showDateSelectionInModal,
    modalLabel
  } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);
  const config = useConfig();

  const minDay = new Date();
  minDay.setDate(
    minDay.getDate() + (ignoreMinDate ? 0 : minDateOffset || config.datePickerMinDateOffset)
  );

  const {
    withDuration,
    value: defaultDuration,
    usePlaceholderAsOption,
    onDurationChange: handleDurationChange
  } = durationProps || {};
  const value = store?.duration ? DurationFactory.create(store?.duration) : defaultDuration;

  const handleDateRangeChange = (start?: string, end?: string): void => {
    if (start) {
      store?.setStartDate(start);
    }
    if (end) {
      store?.setEndDate(end);
    }
    if (onPackageSearchChange) {
      onPackageSearchChange({ changeType: 'Date' });
    }
  };
  const onDurationChange = (numberOfNight?: number): void => {
    if (!!handleDurationChange) {
      handleDurationChange(numberOfNight);
    } else {
      store?.setDuration(numberOfNight);
    }
  };

  const validationErrorComponent =
    store instanceof PackageSearchStore && store.showValidationErrorMessage
      ? (errorKeys?: string[]) => (
          <PackageSearchValidationErrorMessage store={store} errorKeys={errorKeys} />
        )
      : undefined;

  return (
    <>
      {showLabels && (
        <Label htmlFor={withDateRangePicker ? 'rangeDate' : 'monthSearch'}>
          {t(Keys.travelDate)}
        </Label>
      )}
      <DateRangeInput
        customDateFormat={customDateFormat}
        startDate={startDate || store?.fullStartDate}
        endDate={endDate || store?.fullEndDate}
        onChange={onDateRangeChange || handleDateRangeChange}
        minDate={minDate || minDay}
        maxDate={maxDate}
        minDateRangeSpan={minDateRangeSpan}
        maxDateRangeSpan={maxDateRangeSpan}
        dataTestId="serviceSearch_selectDate"
        id="rangeDate"
        seasons={seasons}
        noDatePickerOverflow={noDatePickerOverflow}
        updateStateFromProps={updateStateFromProps}
        disabled={disabled}
        weekDayConstraints={weekDayConstraints}
        duration={{
          withDuration,
          onDurationChange,
          usePlaceholderAsOption,
          value
        }}
        ignoreStartDateForFilter={ignoreStartDateForFilter}
        selectsDisabledDaysInRange={selectsDisabledDaysInRange}
        useAdvancedFeatures={useAdvancedFeatures}
        focusSelectedMonth={focusSelectedMonth}
        fillWithLastDateRangeWhenNoSelection={fillWithLastDateRangeWhenNoSelection}
        clearSelectionsOnOpen={clearSelectionsOnOpen}
        showDateSelectionInModal={showDateSelectionInModal}
        modalLabel={modalLabel}
        validationErrorComponent={validationErrorComponent}
      />
    </>
  );
});

export const DateRangePickerSingleSelect = observer(function DateRangePickerSingleSelect({
  store,
  durationProps,
  showLabels
}: {
  store: PackageSearchStore;
  durationProps?: DateRangeDurationPropsType;
  showLabels?: boolean;
}): JSX.Element {
  const { t } = useTranslation('PackageSearch', fallback, Keys);
  const {
    withDuration,
    value: defaultDuration,
    usePlaceholderAsOption,
    onDurationChange: handleDurationChange
  } = durationProps || {};
  const value = store?.duration ? DurationFactory.create(store?.duration) : defaultDuration;

  const handleSubmit = (
    [startDate, endDate]: [string | undefined, string | undefined],
    duration?: number
  ): void => {
    if (!!startDate) {
      store.setStartDate(startDate);
    }
    if (!!endDate) {
      store.setEndDate(endDate);
    }
    if (!!duration) {
      store.setDuration(duration);
    }
  };

  const onDurationChange = (numberOfNight?: number): void => {
    if (!!handleDurationChange) {
      handleDurationChange(numberOfNight);
    } else {
      store?.setDuration(numberOfNight);
    }
  };

  return (
    <>
      {showLabels && <Label>{t(Keys.travelDate)}</Label>}
      <DateRangeInputSingleField
        onSubmit={handleSubmit}
        duration={{
          withDuration,
          onDurationChange,
          usePlaceholderAsOption,
          value
        }}
        useDateDisplay
        startDate={store?.fullStartDate}
        endDate={store?.fullEndDate}
      />
    </>
  );
});

export const DateMonthPicker = observer(function DateMonthPicker(props: {
  showLabels?: boolean;
  store: PackageSearchStore;
  withDateRangePicker?: boolean;
}): JSX.Element {
  const { showLabels, store, withDateRangePicker } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);
  const minMonth = new Date();
  minMonth.setDate(1);

  return (
    <>
      {showLabels && (
        <Label htmlFor={withDateRangePicker ? 'rangeDate' : 'monthSearch'}>
          {t(Keys.travelDate)}
        </Label>
      )}
      <DatePicker
        dataTestId="packageSearchDatePicker"
        date={store.startDate}
        showMonthYearPicker
        showTwoColumnMonthYearPicker
        onChange={(date: Date): void => store.setDate(date)}
        minDate={minMonth}
        displayFormat="MMM YYYY"
        id="monthSearch"
      />
    </>
  );
});

export const PaxSelector = (props: {
  showLabels?: boolean;
  withCategorySelect?: boolean;
  paxSelection: ApiRoomContainer[];
  setPaxSelection: (selection: ApiRoomContainer[]) => void;
  withChildAgePicker?: boolean;
  withRoomCountSelector?: boolean;
  withGroupRequestForm?: boolean;
  childAgeRestriction?: number;
  minSelectableChildAge?: number;
  withChildrenSelection?: boolean;
  onlyAdultSelect?: boolean;
  showLabelOptionsWithDesc?: boolean;
  staticLabel?: string;
  disabled?: boolean;
  additionalElementsBefore?: Array<JSX.Element>;
  dropdownIcon?: IconDefinition;
  displayInAnotherLayer?: boolean;
  validationErrorComponent?: (errorKeys?: string[]) => ReactNode;
  validateChildrenPaxSelectionOnLeave?: boolean;
  revalidateChildrenOnChangeAfterError?: boolean;
  updatePaxOnLeaveWhenValid?: boolean;
  validateChildrenPaxSelectionOnChange?: boolean;
  updatePaxOnChangeWhenValid?: boolean;
  paxRoomSelectionImperativeHandleRef?: PaxRoomSelectionImperativeHandleRefType;
  childAgePickerHeader?: string;
}): JSX.Element => {
  const {
    showLabels,
    withCategorySelect,
    paxSelection,
    setPaxSelection,
    withChildAgePicker,
    withRoomCountSelector,
    withGroupRequestForm,
    childAgeRestriction,
    minSelectableChildAge,
    withChildrenSelection,
    onlyAdultSelect,
    showLabelOptionsWithDesc,
    staticLabel,
    disabled,
    additionalElementsBefore,
    dropdownIcon,
    displayInAnotherLayer,
    validationErrorComponent,
    validateChildrenPaxSelectionOnLeave,
    revalidateChildrenOnChangeAfterError,
    updatePaxOnLeaveWhenValid,
    validateChildrenPaxSelectionOnChange,
    updatePaxOnChangeWhenValid,
    paxRoomSelectionImperativeHandleRef,
    childAgePickerHeader
  } = props;
  const { t } = useTranslation('PackageSearch', fallback, Keys);

  return (
    <>
      {showLabels && (
        <Label htmlFor="paxSelection">
          {withCategorySelect ? t(Keys.travelers) : t(Keys.paxSelection)}
        </Label>
      )}
      <PaxRoomSelection
        initialOccupancy={paxSelection}
        onChange={(o): void => setPaxSelection(o)}
        withChildAgePicker={withChildAgePicker}
        childAgeRestriction={childAgeRestriction}
        withRoomCountSelector={withRoomCountSelector}
        id="paxSelection"
        withGroupRequestForm={withGroupRequestForm}
        childAgePlaceholder={t(Keys.select)}
        minSelectableChildAge={minSelectableChildAge}
        withChildrenSelection={withChildrenSelection}
        onlyAdultSelect={onlyAdultSelect}
        showLabelOptionsWithDesc={showLabelOptionsWithDesc}
        staticLabel={staticLabel}
        disabled={disabled}
        imperativeHandleRef={paxRoomSelectionImperativeHandleRef}
        additionalElementsBefore={additionalElementsBefore}
        dropdownIcon={dropdownIcon}
        displayInAnotherLayer={displayInAnotherLayer}
        validationErrorComponent={validationErrorComponent}
        validateChildrenPaxSelectionOnLeave={validateChildrenPaxSelectionOnLeave}
        revalidateChildrenOnChangeAfterError={revalidateChildrenOnChangeAfterError}
        updatePaxOnLeaveWhenValid={updatePaxOnLeaveWhenValid}
        validateChildrenPaxSelectionOnChange={validateChildrenPaxSelectionOnChange}
        updatePaxOnChangeWhenValid={updatePaxOnChangeWhenValid}
        childAgePickerHeader={childAgePickerHeader}
      />
    </>
  );
};
